import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Typography, Alert, Modal, Result } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { resetPassword, checkToken } from '../../reducers/user/resources';

import '../login/AntdLogin.css';

const { Title, Link, Text } = Typography;

const SUCCESS_REDIRECT_MS = 5000;

const ResetPassword = ({ history, match }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const { type, token } = match.params;
  const isCreate = type === 'create';
  const title = isCreate ? 'Create Account' : 'Reset Password';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await checkToken(token);
        if (!cancelled && !res.data.isValid) {
          history.push('/login');
        }
      } catch (err) {
        if (!cancelled) history.push('/login');
      }
    })();
    return () => { cancelled = true; };
  }, [token, history]);

  const onFinish = async ({ newPassword }) => {
    setLoading(true);
    setError('');
    try {
      await resetPassword({ token, password: newPassword });
      setSuccess(true);
    } catch (err) {
      setError('Error in resetting your password.');
    } finally {
      setLoading(false);
    }
  };

  const gotoLogin = () => history.push('/login');

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(gotoLogin, SUCCESS_REDIRECT_MS);
    return () => clearTimeout(timer);
  }, [success]);

  return (
    <div className="antd-login-card">
      <div className="login-logo-container">
        <a href="#" onClick={(e) => { e.preventDefault(); gotoLogin(); }}>
          <img src="/assets/images/logo.png" alt="GoHire" className="login-card-logo" />
        </a>
      </div>

      <Form
        form={form}
        name="reset-password"
        onFinish={onFinish}
        layout="vertical"
        size="large"
        requiredMark={false}
      >
        <div className="login-header">
          <Title level={2} className="login-title">{title}</Title>
          <Text type="secondary">
            {isCreate
              ? 'Set a password to finish creating your account.'
              : 'Choose a new password for your account.'}
          </Text>
        </div>

        <Form.Item
          name="newPassword"
          rules={[{ required: true, message: 'Please enter a new password' }]}
        >
          <Input.Password
            prefix={<LockOutlined className="site-form-item-icon" />}
            placeholder="New password"
            autoFocus
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: 'Please confirm your password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('New and confirm password should be the same'));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className="site-form-item-icon" />}
            placeholder="Confirm password"
          />
        </Form.Item>

        {error && <Alert message={error} type="error" showIcon className="login-alert" />}

        <Form.Item>
          <Button type="primary" htmlType="submit" className="login-form-button" loading={loading} block>
            SUBMIT
          </Button>
        </Form.Item>

        <div className="login-form-extras">
          <Link onClick={gotoLogin}>Back to Login</Link>
        </div>
      </Form>

      <Modal
        open={success}
        closable={false}
        maskClosable={false}
        keyboard={false}
        footer={null}
        centered
      >
        <Result
          status="success"
          title="Password Reset"
          subTitle="Your password has been reset. Please try logging in again with your new password."
          extra={(
            <Button type="primary" onClick={gotoLogin}>
              Back to Login
            </Button>
          )}
        />
      </Modal>
    </div>
  );
};

export default connect(() => ({}), () => ({}))(withRouter(ResetPassword));